import type { FC, ReactElement } from 'react';
import { useEffect } from 'react';

import { useChatHistoryFetcher } from '../../../../../store/apiHooks/useChatHistoryFetcher';
import { useLiveSupportMessagesFetcher } from '../../../../../store/apiHooks/useLiveSupportMessagesFetcher';
import { useCoreDispatch } from '../../../../../store/CoreStore';
import { useChatSlice } from '../../../../../store/slices/chat/selectors';
import { chatActions } from '../../../../../store/slices/chat/slice';
import type { Message } from '../../../../../store/slices/chat/types';
import { useConfigSlice } from '../../../../../store/slices/config/selectors';

import { MessagesWrapper } from './MessagesWrapper';

interface MessagesWrapperConnectedProps {
  messages: Message[];
}

export const MessagesWrapperConnected: FC<MessagesWrapperConnectedProps> = ({ messages }): ReactElement => {
  const isLiveSupportRead = useChatSlice.getIsLiveSupportRead();
  const currentUserId = useConfigSlice.playerConfig()?.userId ?? '';
  const currentUserName = useConfigSlice.playerConfig()?.screenName;

  const isGameChatOpened = useChatSlice.getIsGameChatOpened();
  const isSupportChatOpened = useChatSlice.getIsSupportChatOpened();
  const isPrivateTable = useConfigSlice.isPrivateTable();

  const dispatch = useCoreDispatch();
  const gameChatHistoryFetcher = useChatHistoryFetcher(true);
  const liveSupportMessagesFetcher = useLiveSupportMessagesFetcher(false);

  // Fetch game chat history on component mount
  useEffect(() => {
    if (isGameChatOpened) {
      gameChatHistoryFetcher();
    }
  }, [gameChatHistoryFetcher, isGameChatOpened]);

  // Fetch live support chat history on component mount
  useEffect(() => {
    if (isSupportChatOpened) {
      liveSupportMessagesFetcher();
    }
  }, [isSupportChatOpened, liveSupportMessagesFetcher]);

  // Mark live support messages as read when opening live support tab
  useEffect(() => {
    if (!isLiveSupportRead && isSupportChatOpened) {
      dispatch(chatActions.setLiveSupportReadStatus(true));
    }
  }, [dispatch, isLiveSupportRead, isSupportChatOpened]);

  return (
    <MessagesWrapper
      messages={messages}
      currentUserId={currentUserId}
      chatOptionsVisible={isGameChatOpened && isPrivateTable}
      currentUserName={currentUserName}
    />
  );
};
